// @ts-strict-ignore
import { getProperty, setProperty } from 'dot-prop';
import cloneDeep from 'lodash/cloneDeep';
import { getNumericPathIndices } from 'flow/service/actions';
import { ActionType } from '../constants';
import type {
  Action,
  AttributeComponent,
  DataRoute,
  Expression,
  ExpressionTerm,
  SchemaInstance,
  WorkflowProps,
} from '../types';
import {
  getExpressionFunction,
  getIndexAfterSeparating,
  getIsExpressionFunctionString,
  getIsExpressionGroupAwaitingOperands,
  getIsExpressionOperatorString,
  separateFunctionOperands,
} from '../utils';
import type { Attribute } from '~/flow/types';
import { getParentPathAndExpressionItem } from '../../redux/helpers';
import { transformMLAAttributeComponents } from '../transformation/thresholds';
import { isAggregationServiceAction } from '../../../service/services';
import { getDefaultAttribute, resolveAggregationComponents } from './helpers';
import { resolveServiceOrUniversalAttribute } from 'flow/service/thresholds';

export function findDataRouteIdAndRemove({
  dataRouteId,
  originalExpression,
}: {
  dataRouteId: string;
  originalExpression: Expression;
}) {
  return originalExpression.reduce((
    result: Expression,
    expressionObject: ExpressionTerm | ExpressionTerm[],
  ) => {
    if (typeof expressionObject !== 'string'
      && typeof expressionObject !== 'number'
      && 'data_route_id' in expressionObject && expressionObject.data_route_id !== dataRouteId) {
      result.push(expressionObject);
    }

    if (typeof expressionObject === 'number') {
      result.push(expressionObject);
    }

    if (typeof expressionObject === 'string') {
      if (getIsExpressionOperatorString(expressionObject)) {
        /*
          A leading arithmetic operator has nothing left to apply to. A leading min/max operator is the
          group's own function, so the group keeps it and stays a function awaiting operands.
        */
        if (getIsExpressionFunctionString(expressionObject)
          || (result.length > 0 && typeof result[result.length - 1] !== 'string')) {
          result.push(expressionObject);
        }
      } else {
        result.push(expressionObject);
      }
    }

    if (Array.isArray(expressionObject)) {
      const updatedExpression = findDataRouteIdAndRemove({
        dataRouteId,
        originalExpression: expressionObject,
      });

      // the parentheses are the person's own, so taking an item out leaves the group as it was
      result.push(updatedExpression as ExpressionTerm[]);
    }

    return result;
  }, []);
}

function assignDefaultMLAComponents({
  mlaComponents,
}: {
  mlaComponents: AttributeComponent[];
}) {
  return mlaComponents.map(({ attributeComponent, value }) => {
    let updatedDefaultValue;
    if (!value && attributeComponent.default && !attributeComponent.isOptional) {
      if (attributeComponent.dataType === 'csv') {
        updatedDefaultValue = attributeComponent.default;
      } else if (attributeComponent.dataType === 'multi_select') {
        updatedDefaultValue = [attributeComponent.default];
      } else {
        updatedDefaultValue = attributeComponent.default;
      }
    } else {
      updatedDefaultValue = undefined;
    }

    return {
      value: updatedDefaultValue,
      attributeComponent,
    };
  });
}

export function assignDefaultsToDataRoute({
  path,
  dataRoute: oldDataRoute,
  workflowData,
  workflowProps,
  value,
}: {
  path: string;
  dataRoute: DataRoute;
  workflowData: SchemaInstance;
  workflowProps: WorkflowProps;
  value: string | number;
}) {
  // !path means we're creating the route
  const suppliedAttribute = !path && oldDataRoute?.attribute?.id != null
    ? { ...oldDataRoute.attribute }
    : null;

  // Create a new object with values nulled out
  const dataRoute = {
    ...oldDataRoute,
    category: oldDataRoute?.category ?? {
      id: null,
      name: null,
    },
    attribute: suppliedAttribute ?? {
      id: null,
      token: null,
      displayName: '',
      attributeName: '',
      multiLevelAttributeFilter: {
        filterComponents: [],
      },
    },
  };
  const { services, universalAttributes } = workflowProps;
  let sourceAction: Action = null;
  let attribute: Attribute = null;

  if (!path || path === 'sourceId') {
    if (!path) {
      sourceAction = workflowData.actions[dataRoute.sourceId];
    } else {
      dataRoute.sourceId = value as string;
      sourceAction = workflowData.actions[value];
    }

    // Try to determine where the Attribute is coming from and what the data type of it is
    if (sourceAction.type === ActionType.SERVICE) {
      // Attribute belongs to a Service, track it down in the services object.
      const { attributes } = services[sourceAction.service.internalName];
      if (dataRoute.attribute.id === null) {
        // A service can have no eligible attribute at all (json-only, or all archived) -
        // leave the route unattributed rather than reading off an empty list.
        const defaultAttribute = getDefaultAttribute({ action: sourceAction, workflowProps });
        dataRoute.attribute.id = defaultAttribute?.attribute_id ?? null;
        dataRoute.attribute.token = defaultAttribute?.attribute_token ?? null;
      }

      attribute = resolveServiceOrUniversalAttribute(
        attributes,
        universalAttributes,
        dataRoute.attribute.id,
      );
      dataRoute.attribute.displayName = attribute?.display_name;
      dataRoute.attribute.attributeName = attribute?.attribute_name;
      if (attribute?.is_multilevel) {
        const mlaComponents = transformMLAAttributeComponents({
          attributeFilterComponents: attribute.components,
        });
        const remappedMLAComponents = assignDefaultMLAComponents({ mlaComponents });
        dataRoute.attribute.multiLevelAttributeFilter = {
          filterComponents: remappedMLAComponents,
        };
      } else {
        dataRoute.attribute.multiLevelAttributeFilter = null;
      }

      if (isAggregationServiceAction(sourceAction) && attribute) {
        dataRoute.aggregationComponents = resolveAggregationComponents(
          attribute,
          workflowProps.aggregationConfig,
          !path ? dataRoute.aggregationComponents : undefined,
        );
      } else {
        dataRoute.aggregationComponents = [];
      }
    }
  } else if (path === 'attribute.id') {
    /* The attribute is being updated, set the requisite values
     */
    sourceAction = workflowData.actions[dataRoute.sourceId];
    const { attributes: serviceAttributes } = services[sourceAction.service.internalName];
    attribute = resolveServiceOrUniversalAttribute(
      serviceAttributes,
      universalAttributes,
      value as number,
    );
    dataRoute.attribute.id = Number(value);
    dataRoute.attribute.token = attribute?.attribute_token;
    dataRoute.attribute.displayName = attribute?.display_name;
    dataRoute.attribute.attributeName = attribute?.attribute_name;

    if (attribute?.is_multilevel) {
      const mlaComponents = transformMLAAttributeComponents({
        attributeFilterComponents: attribute.components,
      });
      const remappedMLAComponents = assignDefaultMLAComponents({ mlaComponents });
      dataRoute.attribute.multiLevelAttributeFilter = {
        filterComponents: remappedMLAComponents,
      };
    } else {
      dataRoute.attribute.multiLevelAttributeFilter = null;
    }

    if (isAggregationServiceAction(sourceAction) && attribute) {
      dataRoute.aggregationComponents = resolveAggregationComponents(
        attribute,
        workflowProps.aggregationConfig,
      );
    } else {
      dataRoute.aggregationComponents = [];
    }
  }

  return dataRoute;
}

/**
 * Rebuilds the shape the editor draws: the group's min/max between each pair of arguments, plus one
 * trailing operator while the group is still being filled.
 */
export function withFunctionSeparators(
  group: (ExpressionTerm | ExpressionTerm[])[],
  groupFunction: string,
) {
  const separated = separateFunctionOperands(group, groupFunction);
  const lastItem = separated.at(-1);
  const endsInOperator = typeof lastItem === 'string' && getIsExpressionOperatorString(lastItem);

  return endsInOperator ? separated : [...separated, groupFunction];
}

/**
 * Puts a value into an expression and reports where it landed, so callers that need the path do not
 * work it out separately.
 *
 * At a caret the value goes between the items already there; at an item it takes the place after it.
 * A min/max group also gains the operator that draws the new argument's comma, which shifts the
 * argument one position along.
 *
 * atGroupStart marks the caret at a group's opening, where the value belongs inside it, not after.
 */
export function insertIntoExpression({
  expression,
  path,
  value,
  insertion,
  atGroupStart,
}: {
  expression: Expression | null;
  path?: string;
  value: ExpressionTerm | ExpressionTerm[];
  insertion?: boolean;
  /** the path names a group the value belongs inside, rather than a position to follow */
  atGroupStart?: boolean;
}): { expression: Expression; insertedPath: string; wentInsideAwaitingGroup: boolean } {
  // an output attribute carries no expression until something is put in it
  const expressionData = cloneDeep(expression ?? []) as Expression;

  if (expressionData.length === 0) {
    return { expression: [value], insertedPath: '[0]', wentInsideAwaitingGroup: false };
  }

  if (!path?.includes('[')) {
    // no path at all puts the value first, since there is nothing for it to sit after
    return path
      ? {
        expression: [...expressionData, value],
        insertedPath: `[${expressionData.length}]`,
        wentInsideAwaitingGroup: false,
      }
      : {
        expression: [value, ...expressionData],
        insertedPath: '[0]',
        wentInsideAwaitingGroup: false,
      };
  }

  const indices = getNumericPathIndices(path);
  const itemOnPath = getProperty(expressionData, path) as Expression;

  /*
    A caret on a group's opening belongs inside the group, but the index below works out the slot
    after it. The comma the first operand needs is placed here rather than left to the reducer,
    which skips normalising while it previews.
  */
  if (atGroupStart && Array.isArray(itemOnPath)) {
    const group = [value, ...itemOnPath];
    const groupFunction = getExpressionFunction(group);

    setProperty(
      expressionData,
      path,
      groupFunction ? separateFunctionOperands(group, groupFunction) : group,
    );

    return {
      expression: expressionData,
      insertedPath: `${path}[0]`,
      wentInsideAwaitingGroup: false,
    };
  }

  if (insertion && getIsExpressionGroupAwaitingOperands(itemOnPath)) {
    const group = [value, ...itemOnPath];
    const groupFunction = getExpressionFunction(group);

    setProperty(
      expressionData,
      path,
      groupFunction ? withFunctionSeparators(group, groupFunction) : group,
    );

    return {
      expression: expressionData,
      insertedPath: `${path}[0]`,
      wentInsideAwaitingGroup: true,
    };
  }

  const placedIndex = Number(indices.at(-1)) + 1;

  if (indices.length === 1) {
    if (!insertion) {
      expressionData[placedIndex] = value;

      return {
        expression: expressionData,
        insertedPath: `[${placedIndex}]`,
        wentInsideAwaitingGroup: false,
      };
    }

    expressionData.splice(placedIndex, 0, value);

    const rootFunction = getExpressionFunction(expressionData);
    const landedIndex = rootFunction
      ? getIndexAfterSeparating(expressionData, rootFunction, placedIndex)
      : placedIndex;

    return {
      expression: rootFunction
        ? withFunctionSeparators(expressionData, rootFunction)
        : expressionData,
      insertedPath: `[${landedIndex}]`,
      wentInsideAwaitingGroup: false,
    };
  }

  const {
    pathWithoutLastIndex,
    parentExpressionArrayOfTheExpressionOnTheLastIndex: parentGroup,
  } = getParentPathAndExpressionItem({ indices, existingExpression: expressionData });

  if (!insertion) {
    parentGroup[placedIndex] = value;
    setProperty(expressionData, pathWithoutLastIndex, parentGroup);

    return {
      expression: expressionData,
      insertedPath: `${pathWithoutLastIndex}[${placedIndex}]`,
      wentInsideAwaitingGroup: false,
    };
  }

  parentGroup.splice(placedIndex, 0, value);

  const groupFunction = getExpressionFunction(parentGroup);
  const landedIndex = groupFunction
    ? getIndexAfterSeparating(parentGroup, groupFunction, placedIndex)
    : placedIndex;

  setProperty(
    expressionData,
    pathWithoutLastIndex,
    groupFunction ? withFunctionSeparators(parentGroup, groupFunction) : parentGroup,
  );

  return {
    expression: expressionData,
    insertedPath: `${pathWithoutLastIndex}[${landedIndex}]`,
    wentInsideAwaitingGroup: false,
  };
}

export function removeDataRouteFromExpression({
  dataRouteId,
  originalExpression,
}: {
  dataRouteId: string;
  originalExpression: Expression | [];
}) {
  const expressionData = cloneDeep(originalExpression);

  return findDataRouteIdAndRemove({
    dataRouteId,
    originalExpression: expressionData,
  });
}

export function modifyRelevantDataFromDataRouteRemoval({
  targetDataRoute,
  dataRouteId,
  workflowData,
}: {
  targetDataRoute: DataRoute;
  dataRouteId: string;
  workflowData: Partial<SchemaInstance>;
}) {
  const targetAction = workflowData.actions[targetDataRoute.targetId];
  if (targetAction.type === ActionType.OUTPUT) {
    const updatedExpression = removeDataRouteFromExpression({
      dataRouteId,
      originalExpression: targetAction.output.expression,
    });

    workflowData.actions[targetDataRoute.targetId].output.expression = updatedExpression;
  }
}
